'use client';

import { useState } from 'react';

import { CheckCircle, Minus, XCircle } from 'lucide-react';

import { BenefitsSection } from '@/components/sections/benefits-section';
import { FaqSection } from '@/components/sections/faq-section';
import { HelpdeskHero } from '@/components/sections/helpdesk-hero';
import { IndustryCustomers } from '@/components/sections/industry-customers';
import Logos from '@/components/sections/logos';
import Testimonials from '@/components/sections/testimonials';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useTranslation } from '@/lib/i18n/context';
import { cn } from '@/lib/utils';

export default function MobilityPage() {
  const { t } = useTranslation();
  const [hoveredModel, setHoveredModel] = useState<string | null>(null);

  const comparisonData = [
    {
      metric: t.industry['Response Time'],
      agent5: { value: t.industry['Under 10 seconds'], status: 'best' },
      traditional: { value: t.industry['5-15 minutes'], status: 'worst' },
    },
    {
      metric: t.industry['Driver Support'],
      agent5: { value: t.industry['Instant help 24/7'], status: 'best' },
      traditional: { value: t.industry['Limited hours'], status: 'worst' },
    },
    {
      metric: t.industry['Trip Issues'],
      agent5: { value: t.industry['Real-time resolution'], status: 'best' },
      traditional: { value: t.industry['Email-only, slow'], status: 'worst' },
    },

    {
      metric: t.industry['Refund Processing'],
      agent5: { value: t.industry['Automated with rules'], status: 'best' },
      traditional: {
        value: t.industry['Manual review required'],
        status: 'worst',
      },
    },
    {
      metric: t.industry['Peak Hour Scaling'],
      agent5: { value: t.industry['Unlimited capacity'], status: 'best' },
      traditional: { value: t.industry['Long queues'], status: 'worst' },
    },
    {
      metric: t.industry['Language Support'],
      agent5: { value: t.industry['100+ languages'], status: 'best' },
      traditional: {
        value: t.industry['English + a few others'],
        status: 'neutral',
      },
    },
    {
      metric: t.industry['Lost Item Requests'],
      agent5: {
        value: t.industry['Automated logging & tracking'],
        status: 'best',
      },
      traditional: {
        value: t.industry['Phone calls, spreadsheets'],
        status: 'worst',
      },
    },
    {
      metric: t.industry['Cost per Ticket'],
      agent5: {
        value: t.industry['$0.75-$0.90 per resolution'],
        status: 'best',
      },
      traditional: { value: t.industry['$6-12 per ticket'], status: 'worst' },
    },
  ];

  return (
    <>
      <HelpdeskHero
        title={
          <>
            {t.industry['AI customer support for']}
            <br />
            {t.industry['for mobility platforms']}
          </>
        }
        subtitle={
          t.industry[
            'Your riders and drivers need help now—not in 15 minutes. Agent 5 handles trip issues, refunds, and account questions instantly while you scale. Built for platforms that move millions of people daily.'
          ]
        }
        ctaText={t.industry['See how it works for mobility']}
      />

      <Logos />

      <BenefitsSection
        title={t.industry['Support riders and drivers at scale']}
        subtitle={
          t.industry[
            'Handle the support issues that matter when people are on the move.'
          ]
        }
        learnMoreLink="#faq"
        accentColor="emerald"
        backgroundColor="#000000"
        benefits={[
          {
            title: t.industry['Trip issues and ride disputes'],
            description:
              t.industry[
                "Wrong pickup location? Unexpected charges? Driver didn't show? Agent 5 handles complaints, processes refunds, and resolves disputes in real-time—while riders are still in the app."
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'right',
          },
          {
            title: t.industry['Driver support and earnings questions'],
            description:
              t.industry[
                'Drivers ask about earnings, missing trips, account access, and app issues. Agent 5 answers instantly so they can get back to driving—not waiting on hold.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'left',
          },
          {
            title: t.industry['Refunds and cancellations'],
            description:
              t.industry[
                'Cancelled ride? overcharged? Agent 5 reviews the trip, applies your refund policies automatically, and processes returns instantly. No manual review for straightforward cases.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'right',
          },
          {
            title: t.industry['Account access and payment methods'],
            description:
              t.industry[
                "Can't log in? Need to update your card? Agent 5 resets passwords, updates payment info, and fixes account issues—without making users wait."
              ],
            linkText: '',
            linkHref: '#',

            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'left',
          },
          {
            title: t.industry['Lost items and safety reports'],
            description:
              t.industry[
                'Left phone in the car? Safety concern during a ride? Agent 5 logs lost items, connects riders with drivers, and escalates safety issues to your team immediately.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'right',
          },
          {
            title: t.industry['Rental extensions and damage claims'],
            description:
              t.industry[
                'Car rental customers extend reservations, report damage, and ask about insurance coverage. Agent 5 handles the common stuff so your team focuses on complex claims.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'left',
          },
          {
            title: t.industry['Scale during peak demand'],
            description:
              t.industry[
                'Rush hour, holidays, bad weather—support volume spikes 10x. Agent 5 handles the surge without slowing down. Your riders and drivers get help when they need it most.'
              ],
            linkText: '',
            linkHref: '#',
            imagePosition: 'right',
            statCard: {
              value: '77%',
              label: t.industry['Automation Rate'],
              sublabel: t.industry['During peak hours'],
            },
          },
        ]}
      />

      {/* Comparison Section */}
      <section className="bg-white py-32">
        <div className="container mx-auto">
          <div className="mx-auto mb-16 flex max-w-3xl flex-col justify-center gap-7 text-center md:text-center">
            <h2 className="font-serif text-2xl md:text-4xl">
              {t.industry['Why mobility platforms choose Agent 5']}
            </h2>
            <p className="text-muted-foreground text-sm md:text-base">
              {
                t.industry[
                  'Built for businesses where speed matters and support volume is unpredictable.'
                ]
              }
            </p>
          </div>
          <div className="relative overflow-hidden p-8">
            <div className="border-border/50 bg-background/50 relative overflow-hidden border backdrop-blur-sm">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50">
                    <TableHead className="text-foreground font-semibold">
                      {t.industry['What Matters in Mobility']}
                    </TableHead>
                    <TableHead className="text-foreground text-center font-semibold">
                      {t.industry['Agent 5']}
                    </TableHead>
                    <TableHead className="text-foreground text-center font-semibold">
                      {t.industry['Traditional Support']}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comparisonData.map((row, index) => (
                    <TableRow
                      key={index}
                      className="border-border/50"
                      onMouseEnter={() => setHoveredModel(row.metric)}
                      onMouseLeave={() => setHoveredModel(null)}
                    >
                      <TableCell className="text-foreground font-medium">
                        {row.metric}
                      </TableCell>
                      <TableCell className="text-center">
                        <div
                          className={cn(
                            'mx-auto inline-flex items-center gap-2 rounded-full px-3 py-1 transition-all',
                            hoveredModel === row.metric
                              ? 'bg-green-100'
                              : 'bg-transparent',
                          )}
                        >
                          {row.agent5.status === 'best' && (
                            <CheckCircle className="size-4 text-green-600" />
                          )}
                          {row.agent5.status === 'neutral' && (
                            <Minus className="size-4 text-yellow-600" />
                          )}
                          <span className="text-foreground text-sm">
                            {row.agent5.value}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div
                          className={cn(
                            'mx-auto inline-flex items-center gap-2 rounded-full px-3 py-1 transition-all',
                            hoveredModel === row.metric
                              ? 'bg-red-100'
                              : 'bg-transparent',
                          )}
                        >
                          {row.traditional.status === 'worst' && (
                            <XCircle className="size-4 text-red-600" />
                          )}
                          {row.traditional.status === 'neutral' && (
                            <Minus className="size-4 text-yellow-600" />
                          )}
                          <span className="text-muted-foreground text-sm">
                            {row.traditional.value}
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FaqSection
        title={t.industry['Questions from mobility platform operators']}
        subtitle={
          t.industry['Straight answers about handling support at scale.']
        }
        items={[
          {
            question:
              t.industry['Can Agent 5 handle both rider and driver support?'],
            answer:
              t.industry[
                'Yes. We configure separate agents or one agent with different knowledge bases for riders vs drivers. Each gets contextual support tailored to their needs—riders ask about trips, drivers ask about earnings.'
              ],
          },
          {
            question:
              t.industry['How do you handle safety-critical situations?'],
            answer:
              t.industry[
                'Safety reports (accidents, harassment, dangerous driving) are immediately escalated to your safety team with full context. Agent 5 never delays or handles safety issues alone—it connects users to humans and logs everything for investigation.'
              ],
          },
          {
            question: t.industry['Can it process refunds automatically?'],
            answer:
              t.industry[
                'Yes. You set the rules (trip cancelled within 5 minutes, overcharge above $10, etc.) and Agent 5 applies them consistently. Complex disputes go to your team. Simple cases get resolved immediately.'
              ],
          },
          {
            question:
              t.industry['What about peak hours when support volume explodes?'],
            answer:
              t.industry[
                'Agent 5 scales instantly. Whether you get 100 tickets or 10,000 in an hour, response time stays under 10 seconds. No queue, no hiring surge staff, no degraded experience during rush hour or bad weather.'
              ],
          },
          {
            question: t.industry['How does it handle lost item requests?'],
            answer:
              t.industry[
                'Agent 5 logs the item, connects the rider with the driver or vehicle owner, tracks the return process, and follows up. No more lost items falling through the cracks or living in spreadsheets.'
              ],
          },
          {
            question:
              t.industry[
                'Can it help with app troubleshooting and technical issues?'
              ],
            answer:
              t.industry[
                "Yes. Common issues (GPS not working, payment declined, can't book a ride) are diagnosed and resolved with step-by-step guidance. Unusual technical problems get escalated to your support engineers with diagnostic details."
              ],
          },
          {
            question: t.industry['How quickly can we implement this?'],
            answer:
              t.industry[
                'Most mobility platforms are live within 2-3 weeks. That includes API integration with your platform, knowledge base setup, refund rule configuration, and team training. We can move faster if needed.'
              ],
          },
          {
            question:
              t.industry[
                'What languages do you support for international markets?'
              ],
            answer:
              t.industry[
                'Agent 5 supports 100+ languages with automatic detection. Whether your platform operates in one city or 100 countries, riders and drivers get support in their language instantly.'
              ],
          },
        ]}
      />

      <Testimonials />
    </>
  );
}
